import React, { useEffect, useRef, useState } from "react";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import { ErrorMessage, Field, Fieldset, Label } from "../../../design/fieldset";
import { useEvent } from "../../../contexts/EventContext";
import { Event } from "../../../models/Event";
import {
  toLocalDateString,
  toLocalTimeString,
} from "../../../utils/timeString";
import SlateTextArea from "../../../components/SlateTextArea/SlateTextArea";
import { Descendant } from "slate";
import { getPlainText } from "../../../utils/slate";
import { EventTag } from "../../../models/EventTag";
import { SmartTagInput } from "../../Inputs/SmartTagInput";
import UploadEventFiles from "../../CreateEventButton/components/UploadEventFiles";
import { EditableFile } from "../../../models/EditableFile";
import { useFile } from "../../../contexts/FileContext";
import { useLink } from "../../../contexts/LinkContext";
import { createParagraphNode } from "../../../utils/nodeUtils";
import { init } from "@sentry/react";

interface Props {
  initialDate?: Date;
  initialEventTags?: EventTag["_id"][];
  event?: Event;
  onCreate?: (newEventId: string) => Promise<void>;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface FormErrors {
  startDate?: string;
  endDate?: string;
}

const EventDialog: React.FC<Props> = ({ event, initialDate, onCreate, isOpen, setIsOpen, initialEventTags }) => {
  const { createEvent, updateEvent } = useEvent();
  const { createFile } = useFile();
  const { createLink } = useLink();
  const editorRef = useRef(null);

  const [eventTypeId, setEventTypeId] = useState<string | undefined>(event?.eventType);
  const [eventSubtypeId, setEventSubtypeId] = useState<string | undefined>(event?.eventSubtype);
  const [eventTags, setEventTags] = useState<EventTag["_id"][]>(initialEventTags ?? event?.eventTags ?? []);

const getDateValue = () => {
  const date = initialDate ?? event?.eventDate;
  return date ? toLocalDateString(date).slice(0, 10) : "";
};

const getTimeValue = () => {
  const date = initialDate ?? event?.eventDate;
  return date ? toLocalTimeString(date).slice(11, 16) : "";
};

  const [date, setDate] = useState<string>(event || initialDate ? getDateValue() : "");
  const [time, setTime] = useState<string>(event || initialDate ? getTimeValue() : "");
  const [content, setContent] = useState<Descendant[]>(event ? JSON.parse(event.content) : [createParagraphNode()]);
  const [description, setDescription] = useState<string>(event?.description ?? "");
  const [significance, setSignificance] = useState<string>(event?.significance ?? "");
  const [files, setFiles] = useState<EditableFile[]>([]);

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (event && isOpen) {
      setEventTypeId(event.eventType);
      setEventSubtypeId(event.eventSubtype);
      setEventTags(initialEventTags ?? event.eventTags ?? []);
      setDate(getDateValue());
      setTime(getTimeValue());
      setContent(JSON.parse(event.content));
      setDescription(event.description ?? "");
      setSignificance(event.significance ?? "");
    }
  }, [event, isOpen, initialDate, initialEventTags]);

  const validateForm = () => {
    const newErrors: FormErrors = {};
    if (!date.trim()) newErrors.startDate = "Start date is required.";
    if (!time.trim()) newErrors.endDate = "End time is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    setError(null);

    const eventData = {
      eventType: eventTypeId,
      eventSubtype: eventSubtypeId,
      matter: event?.matter,
      eventDate: new Date(`${date}T${time}`),
      content: JSON.stringify(content),
      description,
      significance,
      eventTags,
    };

    try {
      let eventId = event?._id;
      if (event) {
        await updateEvent(event._id, eventData);
      } else if (onCreate) {
        eventId = await createEvent({ ...eventData, participants: [] });
      }

      const fileIds = await Promise.all(
        files.map(async (file) => {
          return await createFile(file.file, {
            type: "evidence",
            name: file.name,
            size: file.file.size,
            mimeType: file.file.type,
            description: file.description ?? "",
            status: "uploading",
            event: eventId!,
          });
        })
      );

      await Promise.all(
        fileIds.map((fileId) =>
          createLink({
            fromType: "File",
            fromId: fileId,
            toType: "Event",
            toId: eventId!,
          })
        )
      );

      if (onCreate) await onCreate(eventId!);
      clearForm();
    } catch (e) {
      console.error(e);
      setError("Failed to save event. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    if (!event) {
      setEventTypeId(undefined);
      setEventSubtypeId(undefined);
      setContent([createParagraphNode()]);
      setDescription("");
      setSignificance("");
      setEventTags([]);
    }
    setIsOpen(false);
    setError(null);
  };

  return (
    <Dialog open={isOpen} onClose={setIsOpen}>
      <DialogTitle>{event ? "Edit Event" : "Create Event"}</DialogTitle>
      {!event && <DialogBody>Fill in the details below to create a new event.</DialogBody>}
      <DialogBody>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <Fieldset>
          <Field>
            <Label htmlFor="date">Date</Label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={isSaving}
              />
              <Input
                id="time"
                type="time"
                step="900"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                disabled={isSaving}
              />
            </div>
            {errors.startDate && <ErrorMessage>{errors.startDate}</ErrorMessage>}
          </Field>

          <Field>
            <Label htmlFor="content">Details</Label>
            <SlateTextArea
              id="content"
              ref={editorRef}
              initialValue={content}
              onChange={(e) => {
                if (e !== content) {
                  setContent(e);
                  setDescription(getPlainText(e));
                }
              }}
            />
          </Field>

          <Field>
            <Label htmlFor="tags">Tags</Label>
            <SmartTagInput
              value={eventTags}
              onChange={setEventTags}
              disabled={isSaving}
            />
          </Field>

          <Field>
            <Label htmlFor="files">Files</Label>
            <UploadEventFiles files={files} setFiles={setFiles} />
          </Field>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={clearForm}>
          Cancel
        </Button>
        <Button disabled={isSaving} onClick={handleSubmit}>
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventDialog;
